import React from 'react';
import MarkdownView from 'react-showdown';
import { useNavigate } from 'react-router-dom';
import { IStorefrontSection } from '../../Api/Models/Products';
import { IRichTextButtonConfig, IRichTextContent } from '../Helpers/contentSectionConfig';
import { renderSectionCells } from './SectionCells';
import { elementStyleFrom, importantStyleCss } from '../Helpers/searchPageConfig';
import { hoverPartFor } from '../Helpers/stylesConfig';

// The CTA's horizontal placement within the section, using the same vocabulary as navPodJustify.
const ctaJustify = (align?: IRichTextButtonConfig['align']): React.CSSProperties['justifyContent'] => {
    switch (align) {
        case 'center': return 'center';
        case 'right': return 'flex-end';
        default: return 'flex-start';
    }
};

// Resting appearance when the merchant configures no style — mirrors the banner overlay CTA pill so
// the two CTAs look alike out of the box. Anything the merchant DOES configure is layered on top
// through the scoped !important rule below (a plain inline style can't outrank the theme's forced
// button rules — same reason CartSummary styles its buttons through a <style> tag).
const CTA_DEFAULT_STYLE: React.CSSProperties = {
    cursor: 'pointer',
    padding: '12px 28px',
    border: 'none',
    borderRadius: '4px',
    textDecoration: 'none',
    backgroundColor: '#0f6cbd',
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    fontFamily: 'inherit',
};

// A content section that renders merchant-authored rich content. Content is now authored as 1-3 typed
// column cells (the same ISectionCell shape as product-section header/footer cells) carried on the
// section's `headerCells`; they render in a flex row via the shared cell renderer — equal columns
// (flex: 1 1 0) unless a cell's styleProps pin a width, wrapping on narrow viewports.
//
// Backward compat: a section authored before the cell rework has no headerCells but a legacy
// contentConfig `{ markdown }`; that renders as a single markdown block exactly as before.
//
// The contentConfig may additionally carry ONE optional CTA button (`{ button: { label, url, ... } }`)
// which renders before or after the content. With no button configured the output is unchanged from
// before the CTA existed — no extra wrapper element, so existing sections keep their exact layout
// (a flex wrapper would, for instance, stop markdown paragraph margins from collapsing).
const RichTextSection: React.FC<{ section: IStorefrontSection; legacyConfig: IRichTextContent | null }> = ({ section, legacyConfig }) => {
    const nav = useNavigate();
    const button = legacyConfig?.button;
    const hasButton = !!button?.label?.trim();

    // Per-instance class so two rich-text sections on the same page can carry different button
    // styling. useId's value contains ':' (invalid in a selector), so strip everything unsafe.
    const instanceId = React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    const cls = `richtext-cta-${instanceId}`;

    // Base + hover come from the same recipe as the cart buttons: resting declarations also cover
    // :hover (so a configured button doesn't snap back to the theme color), then the referenced
    // named style's `hover` part layers on top. Empty style => no rule emitted at all.
    const baseCss = React.useMemo(() => {
        const s = elementStyleFrom(button?.style);
        return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [button?.style]);
    const hoverCss = React.useMemo(() => {
        const s = elementStyleFrom(hoverPartFor(button?.style), 'hover');
        return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [button?.style]);

    // External URLs open a new tab (noopener/noreferrer so a merchant URL can't get a window
    // handle); internal paths navigate in-app. Same helper as the banner CTA.
    const navigateTo = (url?: string) => {
        if (!url) return;
        if (url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
        else nav(url);
    };

    const cells = section.headerCells;
    const markdown = legacyConfig?.markdown;
    const hasCells = !!cells && cells.length > 0;
    const hasMarkdown = !!markdown && !!markdown.trim();

    // No CTA: byte-for-byte the pre-CTA render paths.
    if (!hasButton) {
        if (hasCells) {
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
                    {renderSectionCells(cells!)}
                </div>
            );
        }
        if (!hasMarkdown) return null;
        return <MarkdownView markdown={markdown!} />;
    }

    // A section with a CTA but no content at all still renders the CTA.
    const body = hasCells
        ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
                {renderSectionCells(cells!)}
            </div>
        )
        : hasMarkdown ? <MarkdownView markdown={markdown!} /> : null;

    const cta = (
        <div style={{ display: 'flex', justifyContent: ctaJustify(button?.align) }}>
            <button type="button" className={cls} style={CTA_DEFAULT_STYLE} onClick={() => navigateTo(button?.url)}>
                {button?.label}
            </button>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
            {(baseCss || hoverCss) && (
                <style>{`${baseCss ? `.${cls}, .${cls}:hover { ${baseCss} }` : ''}${hoverCss ? `.${cls}:hover { ${hoverCss} }` : ''}`}</style>
            )}
            {button?.position === 'start' && cta}
            {body}
            {button?.position !== 'start' && cta}
        </div>
    );
};

export default RichTextSection;
